import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import CountUp from 'react-countup';
import { useMemoizedFn } from 'ahooks';

// 颜色值
const getChartColor = (percent) => {
  let color = 'l(0) 0:#AEF8DF 1:#3EDEA9';
  if (percent < 0.85) {
    color = 'l(0) 0:#FFE68C 1:#F9B34C';
  }
  if (percent < 0.15) {
    color = 'l(0) 0:#FFA0A0 1:#F45151';
  }
  return color;
};

/**
 * 健康度图表
 * @param {Object} props props
 * @param {number} props.percent 健康度范围(小数 0-1)
 */
export default function HealthGauge({ percent: pct }) {
  const [rotate, setRotate] = useState(0);
  const drawRef = useRef();
  const [isReady, setIsReady] = useState(false);

  const percent = pct < 20 / 180 ? 20 / 180 : pct; // 过小保留20度
  const ref = useRef(null);

  // 中心文字颜色
  const textColor = useMemo(() => {
    let color = 'rgba(16, 184, 128, 0.9)';
    if (percent < 0.85) {
      color = 'rgba(244, 157, 11, .9)';
    }
    if (percent < 0.15) {
      color = 'rgba(238, 68, 68, 0.9)';
    }
    return color;
  }, [percent]);

  // 背景
  const bg = useMemo(() => {
    let img = require('./images/green-back@2x.png');
    if (percent < 0.85) {
      img = require('./images/yellow-back@2x.png');
    }
    if (percent < 0.15) {
      img = require('./images/red-back@2x.png');
    }

    return img;
  }, [percent]);

  // 指针图片
  const cursor = useMemo(() => {
    let img = require('./images/green-front@2x.png');
    if (percent < 0.85) {
      img = require('./images/yellow-front@2x.png');
    }
    if (percent < 0.15) {
      img = require('./images/red-front@2x.png');
    }

    return img;
  }, [percent]);

  const doInitDraw = useMemoizedFn(async () => {
    const { Gauge } = await import('@antv/g2plot');
    if (ref.current) {
      const gauge = new Gauge(ref.current, {
        percent: 0,
        padding: [0, 2],
        animation: true,
        range: {
          width: 12,
        },
        startAngle: Math.PI,
        endAngle: 2 * Math.PI,
        indicator: null,
        statistic: null,
      });
      gauge.render();
      drawRef.current = gauge;
      setIsReady(true);
    }
  });

  useEffect(() => {
    doInitDraw();
    return () => {
      drawRef.current?.destroy();
    };
  }, [doInitDraw]);

  useEffect(() => {
    setTimeout(() => {
      setRotate(percent * 180 - 10);
    }, 300);
  }, [percent]);

  useEffect(() => {
    if (isReady) {
      drawRef.current.update({
        percent,
        range: {
          color: [getChartColor(percent), 'transparent'],
        },
      });
    }
  }, [percent, isReady]);

  return (
    <Root bg={bg}>
      <Chart ref={ref} />
      <CursorImg rotate={rotate} src={cursor} />
      <Center color={textColor}>
        <CountUp end={pct * 100} duration={2} />分
      </Center>
    </Root>
  );
}

const Root = styled.div`
  width: 74px;
  height: 74px;
  background-image: url(${(props) => props.bg});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
  position: relative;
`;

const Chart = styled.div`
  width: 76px;
  height: 76px;
  margin-top: -18px;
  margin-left: -1px;
`;

const CursorImg = styled.img`
  position: absolute;
  z-index: 1;
  width: 74px;
  height: 74px;
  top: 0;
  left: 0;
  transition: 0.4s all cubic-bezier(0.645, 0.045, 0.355, 1);
  transform: rotate(${(props) => props.rotate}deg);
`;

const Center = styled.div`
  position: absolute;
  z-index: 2;
  left: 0;
  right: 0;
  margin: auto;
  width: 40px;
  top: 30px;
  font-weight: bold;
  font-size: 10px;
  line-height: 14px;
  color: ${(props) => props.color};
  display: flex;
  justify-content: center;
`;
